import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronDown, faBuilding, faSpinner, faUsers, faCheck, faExchangeAlt, faCrown, faLink
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { getLinkedSuppliers, LinkedSupplier } from '../../services/linkedSuppliersApi';

interface CompactSupplierSwitcherProps {
  currentMasterToken: string;
  currentSupplierName: string;
  onSwitchSupplier: (masterToken: string, supplierName: string) => void;
  className?: string;
}

const CompactSupplierSwitcher: React.FC<CompactSupplierSwitcherProps> = ({
  currentMasterToken,
  currentSupplierName,
  onSwitchSupplier,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [allSuppliers, setAllSuppliers] = useState<LinkedSupplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeSupplierName, setActiveSupplierName] = useState(currentSupplierName);
  const [currentSupplierType, setCurrentSupplierType] = useState<'master' | 'linked' | 'unknown'>('unknown');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load linked suppliers with NEW API structure
  const loadLinkedSuppliers = async () => {
    if (!currentMasterToken) {
      console.log('❌ CompactSupplierSwitcher: No currentMasterToken provided');
      return;
    }
    
    console.log('🔗 [REBUILD] CompactSupplierSwitcher: Loading for masterToken:', currentMasterToken);
    console.log('👤 Current supplier name:', currentSupplierName);
    
    setLoading(true);
    
    try {
      const response = await getLinkedSuppliers(currentMasterToken);
      console.log('✅ [REBUILD] CompactSupplierSwitcher API response:', response);
      
      if (response.success && response.linkedSuppliers) {
        console.log('📊 [REBUILD] Found suppliers:', response.linkedSuppliers.length);
        
        // Store ALL suppliers (master + linked)
        setAllSuppliers(response.linkedSuppliers);
        
        // Find current supplier type
        const currentSupplier = response.linkedSuppliers.find(s => s.name === currentSupplierName);
        if (currentSupplier) {
          setCurrentSupplierType(currentSupplier.supplierType);
          console.log('🎯 [REBUILD] Current supplier type:', currentSupplier.supplierType);
        }
        
        // Debug: log each supplier with type
        response.linkedSuppliers.forEach((supplier, index) => {
          console.log(`📋 [REBUILD] Supplier ${index + 1}:`, {
            name: supplier.name,
            type: supplier.supplierType,
            isPrimary: supplier.isPrimary,
            isCurrent: supplier.isCurrent
          });
        });
        
      } else {
        console.log('❌ [REBUILD] API response failed or no suppliers');
        setAllSuppliers([]);
        setCurrentSupplierType('unknown');
      }
    } catch (err: any) {
      console.error('❌ [REBUILD] CompactSupplierSwitcher: Error loading:', err);
      setAllSuppliers([]);
      setCurrentSupplierType('unknown');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLinkedSuppliers();
  }, [currentMasterToken]);

  // Check for active supplier context with better synchronization
  useEffect(() => {
    const activeContext = sessionStorage.getItem('activeSupplierContext');
    const supplierInfo = sessionStorage.getItem('supplierInfo');
    
    if (activeContext) {
      try {
        const context = JSON.parse(activeContext);
        setActiveSupplierName(context.name);
        setCurrentSupplierType(context.supplierType || 'linked');
        console.log('🔄 [REBUILD] Context loaded:', context);
      } catch (e) {
        console.log('❌ [REBUILD] Context parse error, fallback to currentSupplierName');
        setActiveSupplierName(currentSupplierName);
        setCurrentSupplierType('master'); // Default to master when no context
      }
    } else if (supplierInfo) {
      try {
        const info = JSON.parse(supplierInfo);
        setActiveSupplierName(info.name);
        // If no activeContext but we have supplierInfo, it's the master account
        setCurrentSupplierType(info.supplierType || 'master');
        console.log('🔄 [REBUILD] SupplierInfo loaded (master account):', info);
      } catch (e) {
        setActiveSupplierName(currentSupplierName);
        setCurrentSupplierType('master');
      }
    } else {
      setActiveSupplierName(currentSupplierName);
      setCurrentSupplierType('master'); // Default to master when no context
    }
  }, [currentSupplierName]);

  // Watch for session storage changes from other components
  useEffect(() => {
    const handleStorageChange = () => {
      console.log('🔄 [REBUILD] Storage changed, reloading context');
      const activeContext = sessionStorage.getItem('activeSupplierContext');
      const supplierInfo = sessionStorage.getItem('supplierInfo');
      
      if (activeContext) {
        try {
          const context = JSON.parse(activeContext);
          setActiveSupplierName(context.name);
          setCurrentSupplierType(context.supplierType || 'linked');
        } catch (e) {
          console.log('❌ [REBUILD] Storage change parse error');
        }
      } else if (supplierInfo) {
        // No activeContext = master account
        try {
          const info = JSON.parse(supplierInfo);
          setActiveSupplierName(info.name);
          setCurrentSupplierType(info.supplierType || 'master');
        } catch (e) {
          setCurrentSupplierType('master');
        }
      } else {
        setCurrentSupplierType('master');
      }
    };

    // Listen for custom supplier switch events
    const handleSupplierSwitched = (event: any) => {
      console.log('🔄 [REBUILD] Supplier switched event received:', event.detail);
      setActiveSupplierName(event.detail.name);
      setCurrentSupplierType(event.detail.supplierType);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('supplierSwitched', handleSupplierSwitched);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('supplierSwitched', handleSupplierSwitched);
    };
  }, []);

  const handleSupplierSwitch = (supplier: LinkedSupplier) => {
    if (!supplier.canSwitchTo) return;
    
    console.log('🔄 [REBUILD] Switching to supplier:', supplier.name, 'type:', supplier.supplierType);
    
    // **CRITICAL FIX: Update session storage IMMEDIATELY**
    const supplierContext = {
      name: supplier.name,
      supplierId: supplier.supplierId,
      masterToken: supplier.masterToken,
      supplierType: supplier.supplierType,
      switchedAt: new Date().toISOString()
    };
    
    sessionStorage.setItem('activeSupplierContext', JSON.stringify(supplierContext));
    sessionStorage.setItem('supplierInfo', JSON.stringify({
      name: supplier.name,
      supplierId: supplier.supplierId,
      masterToken: supplier.masterToken
    }));
    
    console.log('✅ Session storage updated:', supplierContext);
    
    // **TRIGGER CUSTOM EVENT for immediate dashboard reload**
    window.dispatchEvent(new CustomEvent('supplierContextChanged', { 
      detail: supplierContext 
    }));
    
    setIsOpen(false);
    setActiveSupplierName(supplier.name);
    setCurrentSupplierType(supplier.supplierType);
    
    // **FORCE RE-RENDER: Trigger callback with delay to ensure session is updated**
    setTimeout(() => {
      onSwitchSupplier(supplier.masterToken, supplier.name);
    }, 100);
  };

  // NEW: Get master and linked suppliers separately
  const masterSupplier = allSuppliers.find(s => s.supplierType === 'master');
  const linkedSuppliers = allSuppliers.filter(s => s.supplierType === 'linked');
  const hasLinkedSuppliers = linkedSuppliers.length > 0;
  const totalAvailableSuppliers = allSuppliers.length;

  console.log('🔄 [REBUILD] CompactSupplierSwitcher render state:');
  console.log('- currentMasterToken:', currentMasterToken);
  console.log('- currentSupplierName:', currentSupplierName);
  console.log('- activeSupplierName:', activeSupplierName);
  console.log('- currentSupplierType:', currentSupplierType);
  console.log('- allSuppliers total:', allSuppliers.length);
  console.log('- masterSupplier:', masterSupplier?.name || 'None');
  console.log('- linkedSuppliers:', linkedSuppliers.length);
  console.log('- hasLinkedSuppliers:', hasLinkedSuppliers);
  console.log('- loading:', loading);

  // Loading state
  if (loading) {
    return (
      <div className={`flex items-center space-x-1.5 px-2 py-1.5 bg-gray-700 rounded-md ${className}`}>
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-300 text-xs" />
        <span className="text-gray-300 text-xs">Lade...</span>
      </div>
    );
  }

  // **ALWAYS SHOW SUPPLIER INFO**
  const canShowDropdown = totalAvailableSuppliers > 1; // Show dropdown if more than just current supplier

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => canShowDropdown && setIsOpen(!isOpen)}
        disabled={loading || !canShowDropdown}
        className={`flex items-center space-x-1.5 px-2 py-1.5 bg-transparent text-gray-300 hover:text-white rounded-md transition-all duration-200 group border-none shadow-none ${
          canShowDropdown ? 'hover:bg-gray-700/50 cursor-pointer' : 'cursor-default'
        }`}
      >
        <FontAwesomeIcon 
          icon={currentSupplierType === 'master' ? faCrown : faBuilding} 
          className={`text-xs ${currentSupplierType === 'master' ? 'text-yellow-400' : 'text-gray-400'} group-hover:text-gray-300`} 
        />
        
        <div className="flex flex-col text-left min-w-0">
          <span className="text-[10px] text-gray-500 group-hover:text-gray-400 leading-tight">
            {currentSupplierType === 'master' ? 'Hauptkonto' : 
             currentSupplierType === 'linked' ? 'Verknüpft' : 'Lieferant'}
          </span>
          <span className="text-xs font-medium truncate max-w-28 leading-tight">
            {activeSupplierName}
          </span>
        </div>
        
        {canShowDropdown && (
          <div className="flex items-center space-x-0.5">
            <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded-full font-medium">
              +{totalAvailableSuppliers - 1}
            </span>
            <FontAwesomeIcon 
              icon={faChevronDown} 
              className={`text-gray-400 text-[10px] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
            />
          </div>
        )}
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && canShowDropdown && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-1 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-[9999] overflow-visible"
            style={{ 
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              maxHeight: 'calc(100vh - 100px)'
            }}
          >
            {/* Header */}
            <div className="px-3 py-2 bg-gradient-to-r from-gray-700 to-gray-800 text-white">
              <div className="flex items-center space-x-1.5">
                <FontAwesomeIcon icon={faExchangeAlt} className="text-gray-300 text-[10px]" />
                <h4 className="text-xs font-semibold">Lieferant wechseln</h4>
              </div>
              <p className="text-[10px] text-gray-300 mt-0.5">
                Wählen Sie einen Lieferanten aus
              </p>
            </div>

            {/* Suppliers List */}
            <div className="py-1 max-h-72 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              
              {/* Master Supplier */}
              {masterSupplier && (
                <button
                  onClick={() => handleSupplierSwitch(masterSupplier)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center justify-between group"
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-7 h-7 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-md flex items-center justify-center">
                      <FontAwesomeIcon icon={faCrown} className="text-yellow-600 text-[10px]" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 text-xs">{masterSupplier.name}</div>
                      <div className="flex items-center space-x-1.5 text-[10px] text-gray-500">
                        {masterSupplier.kreditorNumber && (
                          <span>Kreditor: {masterSupplier.kreditorNumber}</span>
                        )}
                        <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded-full font-medium text-[9px]">
                          Hauptkonto
                        </span>
                      </div>
                    </div>
                  </div>
                  {activeSupplierName === masterSupplier.name && (
                    <div className="flex items-center space-x-1">
                      <span className="text-[10px] text-green-600 font-medium">Aktiv</span>
                      <FontAwesomeIcon icon={faCheck} className="text-green-600 text-[10px]" />
                    </div>
                  )}
                </button>
              )}

              {/* Divider if both master and linked exist */}
              {masterSupplier && linkedSuppliers.length > 0 && (
                <div className="border-t border-gray-100 my-1"></div>
              )}

              {/* Linked Suppliers */}
              {linkedSuppliers.length > 0 ? (
                linkedSuppliers.map((supplier: LinkedSupplier) => (
                  <button
                    key={supplier.supplierId}
                    onClick={() => handleSupplierSwitch(supplier)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center justify-between group"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="w-7 h-7 bg-gradient-to-br from-blue-100 to-blue-200 rounded-md flex items-center justify-center">
                        <FontAwesomeIcon icon={faLink} className="text-blue-600 text-[10px]" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 text-xs">{supplier.name}</div>
                        <div className="flex items-center space-x-1.5 text-[10px] text-gray-500">
                          {supplier.kreditorNumber && (
                            <span>Kreditor: {supplier.kreditorNumber}</span>
                          )}
                          <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium text-[9px]">
                            Verknüpft
                          </span>
                        </div>
                      </div>
                    </div>
                    {activeSupplierName === supplier.name && (
                      <div className="flex items-center space-x-1">
                        <span className="text-[10px] text-green-600 font-medium">Aktiv</span>
                        <FontAwesomeIcon icon={faCheck} className="text-green-600 text-[10px]" />
                      </div>
                    )}
                  </button>
                ))
              ) : (
                !masterSupplier && (
                  <div className="px-3 py-4 text-center">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <FontAwesomeIcon icon={faUsers} className="text-gray-400 text-xs" />
                    </div>
                    <div className="text-xs text-gray-600 font-medium mb-1">
                      Keine Lieferanten gefunden
                    </div>
                    <div className="text-[10px] text-gray-500 leading-relaxed">
                      Keine verfügbaren Lieferanten zum Wechseln.
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Footer */}
            <div className="px-3 py-2 bg-gray-50 border-t border-gray-200">
              <div className="text-[10px] text-gray-500 text-center">
                <FontAwesomeIcon icon={faBuilding} className="mr-1 text-[9px]" />
                {totalAvailableSuppliers} Lieferant{totalAvailableSuppliers !== 1 ? 'en' : ''} verfügbar
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CompactSupplierSwitcher;